import { useCallback, useEffect, useState } from "react";

import {
  Avatar,
  CopyableField,
  DateTimeTz,
  LoadingIcon,
  RefreshButton,
} from "@web-app/components";
import { timeZoneMap } from "@web-app/utils/helpers";
import http from "@web-app/utils/http";

type EZCaterDeliveryPropsType = {
  orderId: string;
  timezone: keyof typeof timeZoneMap;
};

type EZCaterDeliveryType = {
  cancelled: boolean;
  catererPickupVerification: {
    readyForPickupImageUrl: string;
  } | null;
  distance: string;
  driverAskFor: string;
  drivers: Array<{
    externalId: string;
    firstName: string;
    id: string;
    lastName: string;
    phone: string;
  }>;
  dropoffAddress: {
    city: string;
    coordinate: {
      latitude: number;
      longitude: number;
    };
    id: string;
    instructions: { text: string | null };
    locationName: string;
    phone: string;
    postalCode: string;
    state: string;
    street1: string;
    street2: string | null;
    street3: string | null;
  };
  eventAt: string;
  id: string;
  order: {
    forRelish: boolean;
    itemCount: number;
    orderNumber: string;
    subtotal: number;
    tip: number;
  };
  pickupAddress: {
    city: string;
    coordinate: {
      latitude: number;
      longitude: number;
    };
    id: string;
    instructions: { text: string | null };
    locationName: string;
    phone: string;
    postalCode: string;
    state: string;
    street1: string;
    street2: string | null;
    street3: string | null;
  };
  pickupInstructions: string | null;
  priority: string;
  requestedPickupAt: string;
  status: string;
  timeZoneIdentifier: string;
};

type EZCaterDeliveryResponse = {
  data: {
    delivery: EZCaterDeliveryType;
  };
};

export default function EZCaterDeliveryInfo({
  orderId,
  timezone,
}: EZCaterDeliveryPropsType) {
  const [data, setData] = useState<EZCaterDeliveryType | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getDelivery = useCallback(async () => {
    try {
      const resp = await http.get<EZCaterDeliveryResponse>(
        `/integrations/ezcater/orders/${orderId}/delivery`,
      );

      setData(resp.data.data.delivery);
      setIsLoading(false);
    } catch {
      setIsLoading(false);
      setData(null);
    }
  }, [orderId]);

  useEffect(() => {
    getDelivery();
  }, [getDelivery]);

  const onRefresh = () => {
    setIsLoading(true);
    getDelivery();
  };

  const getDriverName = () => {
    if (data?.drivers && data.drivers.length > 0) {
      const driver = data.drivers[0];
      return `${driver.firstName} ${driver.lastName}`;
    }
    return "";
  };

  const getDriverPhone = () => {
    if (data?.drivers && data.drivers.length > 0) {
      return data.drivers[0].phone;
    }
    return "";
  };

  const formatAddress = (
    address: EZCaterDeliveryType["pickupAddress"] | undefined,
  ) => {
    if (!address) return "N/A";
    const parts = [
      address.street1,
      address.street2,
      address.street3,
      address.city,
      address.state,
      address.postalCode,
    ].filter(Boolean);
    return parts.join(", ");
  };

  return (
    <div>
      {isLoading ? (
        <LoadingIcon className="w-6 md:w-8 h-6 md:h-8 m-auto mt-10" />
      ) : (
        <div>
          {data === null ? (
            <div className="text-xs md:text-sm w-full flex items-center justify-center text-gray-500">
              No EZCater delivery found.
            </div>
          ) : (
            <div>
              <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Status</span>
                  <span className="tw-details-field-value">
                    {data.status.charAt(0).toUpperCase() +
                      data.status.slice(1).toLowerCase()}
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Priority</span>
                  <span className="tw-details-field-value">
                    {data.priority.charAt(0).toUpperCase() +
                      data.priority.slice(1).toLowerCase()}
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Distance</span>
                  <span className="tw-details-field-value">
                    {data.distance || "N/A"}
                  </span>
                </div>
                {data.cancelled && (
                  <div className="grid grid-cols-[1fr_2fr] gap-4">
                    <span className="tw-details-field-title">Cancelled</span>
                    <span className="tw-details-field-value !text-red-700">
                      Yes
                    </span>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Order Number</span>
                  <span className="tw-details-field-value">
                    {data.order.orderNumber}
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Item Count</span>
                  <span className="tw-details-field-value">
                    {data.order.itemCount}
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Subtotal</span>
                  <span className="tw-details-field-value">
                    ${data.order.subtotal?.toFixed(2) ?? "0.00"}
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Tip</span>
                  <span className="tw-details-field-value">
                    ${data.order.tip?.toFixed(2) ?? "0.00"}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">
                    Requested Pickup At
                  </span>
                  <span className="tw-details-field-value">
                    <DateTimeTz
                      start={new Date(data.requestedPickupAt)}
                      timezone={timezone}
                      presentation="default"
                    />
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Event At</span>
                  <span className="tw-details-field-value">
                    <DateTimeTz
                      start={new Date(data.eventAt)}
                      timezone={timezone}
                      presentation="default"
                    />
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
                <div className="grid pb-2">
                  <span className="tw-details-field-title font-semibold">
                    Pickup Details:
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Location Name</span>
                  <span className="tw-details-field-value">
                    {data.pickupAddress.locationName}
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Address</span>
                  <span className="tw-details-field-value break-words">
                    {formatAddress(data.pickupAddress)}
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Phone</span>
                  <span className="tw-details-field-value">
                    {data.pickupAddress.phone}
                  </span>
                </div>
                {data.pickupAddress.instructions?.text && (
                  <div className="grid grid-cols-[1fr_2fr] gap-4">
                    <span className="tw-details-field-title">Instructions</span>
                    <span className="tw-details-field-value break-words whitespace-pre-wrap">
                      {data.pickupAddress.instructions.text}
                    </span>
                  </div>
                )}
                {data.pickupInstructions && (
                  <div className="grid grid-cols-[1fr_2fr] gap-4">
                    <span className="tw-details-field-title">
                      Pickup Instructions
                    </span>
                    <span className="tw-details-field-value break-words whitespace-pre-wrap">
                      {data.pickupInstructions}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
                <div className="grid pb-2">
                  <span className="tw-details-field-title font-semibold">
                    Drop-off Details:
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Location Name</span>
                  <span className="tw-details-field-value">
                    {data.dropoffAddress.locationName}
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Address</span>
                  <span className="tw-details-field-value break-words">
                    {formatAddress(data.dropoffAddress)}
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Phone</span>
                  <span className="tw-details-field-value">
                    {data.dropoffAddress.phone}
                  </span>
                </div>
                {data.dropoffAddress.instructions?.text && (
                  <div className="grid grid-cols-[1fr_2fr] gap-4">
                    <span className="tw-details-field-title">Instructions</span>
                    <span className="tw-details-field-value break-words whitespace-pre-wrap">
                      {data.dropoffAddress.instructions.text}
                    </span>
                  </div>
                )}
                {data.driverAskFor && (
                  <div className="grid grid-cols-[1fr_2fr] gap-4">
                    <span className="tw-details-field-title">
                      Driver Ask For
                    </span>
                    <span className="tw-details-field-value break-words">
                      {data.driverAskFor}
                    </span>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-[1fr_2fr] gap-4 px-4 py-4 border-b border-gray-200">
                <span className="tw-details-field-title">Assignee</span>
                <span className="tw-details-field-value font-semibold flex flex-col gap-2">
                  <span className="flex items-center gap-2">
                    {!getDriverName() && <span>No Assignee</span>}
                    {getDriverName() && (
                      <>
                        <span>
                          <Avatar image={undefined} size={10} />
                        </span>
                        <span className="flex flex-col">
                          <span className="tw-details-field-value font-semibold break-words">
                            {getDriverName()}
                          </span>
                          <CopyableField
                            icon="fasPhone"
                            value={getDriverPhone()}
                            appearance="inline"
                            className="text-xs font-normal"
                          />
                        </span>
                      </>
                    )}
                  </span>
                </span>
              </div>

              <div className="flex justify-end items-center gap-2 pr-4 pt-2">
                <span className="text-sm text-gray-700">Refresh</span>
                <RefreshButton onClick={onRefresh} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
